import React, { useEffect, useRef, useState } from 'react';
import './Waveform.css';
import { Grid, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { storiesStyles } from './style';
import ProgressBar from '../components/ProgressBar';
import hoverStyle from './hover.module.css';
import StoriesComponent from '../components/Stories';
import { getStories } from '../services/storyService';
import useTopDownPreloader from '../utils/useTopDownPreloader';

import { dedupeStories, goFrontPage, openLink, openShop } from '../utils';

const width = window.innerWidth;
const SCROLL_UNLOCK = 3;
const INITIAL_LIMIT = 30;

const Stories = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [stories, setStories] = useState(undefined);

	const classes = storiesStyles();

	const [scrollExtra, setScrollExtra] = useState(0);
	const sentinelRef = useRef(null);

	// Single fetch trigger keyed on location.state — runs once on mount and
	// again whenever Digital is clicked (which navigates with a new reshuffle
	// timestamp).
	//
	// The `cancelled` flag protects against two known double-fetch cases:
	//  1. React 18 StrictMode in dev double-invokes effects on mount. The
	//     first fetch's late result would otherwise overwrite the second
	//     (newer) one because ?order=random returns a different shuffle.
	//  2. Quick consecutive Digital clicks could land overlapping fetches.
	//     Only the most-recent fetch is allowed to call setStories.
	useEffect(() => {
		let cancelled = false;
		setStories(undefined);
		setScrollExtra(0);
		(async () => {
			const data = await getStories();
			if (cancelled) return;
			setStories(dedupeStories(data));
		})();
		if (location.state?.reshuffle) {
			window.scrollTo(0, 0);
		}
		return () => {
			cancelled = true;
		};
	}, [location.state]);

	// Top-down preloader: fetches images in the background and reports the
	// highest contiguous loaded index so we render strictly top-down with no
	// layout shift (each <img> mounts when its bytes are already cached).
	const eligibleLimit = INITIAL_LIMIT + scrollExtra;
	const visibleEnd = useTopDownPreloader(stories, eligibleLimit);

	useEffect(() => {
		if (!sentinelRef.current || !stories) return undefined;
		if (visibleEnd >= stories.length) return undefined;

		const obs = new IntersectionObserver(
			entries => {
				if (entries[0].isIntersecting) {
					setScrollExtra(prev => prev + SCROLL_UNLOCK);
				}
			},
			{ rootMargin: '300px' }
		);
		obs.observe(sentinelRef.current);
		return () => obs.disconnect();
	}, [visibleEnd, stories]);

	const visibleStories = stories ? stories.slice(0, visibleEnd) : [];

	const storiesPaginated = visibleStories.map((item, index) => (
		<StoriesComponent
			key={`${item.uuid || index}-${index}`}
			item={item}
			classes={classes}
			hoverStyle={hoverStyle}
			storyImageLoaded={null}
			index={index}
			navigate={navigate}
			eager
		/>
	));

	return (
		<>
			<ProgressBar active={!stories || visibleEnd === 0} />
			<div>
				{stories && (
					<Masonry
						breakpointCols={1}
						className={classes.masonryGrid}
						columnClassName={classes.masonryGridColumn}
					>
						{storiesPaginated}
					</Masonry>
				)}
				<div ref={sentinelRef} style={{ height: 1, width: '100%' }} />
				<Grid className={classes.centerText}>
					<Grid onClick={openShop} className={classes.rightText} style={{ right: width > 992 ? 20 : 0 }}>
						<Typography className={classes.text}>Shop</Typography>
					</Grid>
					<Grid onClick={openLink} className={classes.leftText} style={{ left: width > 992 ? 20 : 0 }}>
						<Typography className={classes.text}>I Never Read,</Typography>
					</Grid>
					<Grid onClick={() => goFrontPage(navigate)} className={classes.bottomText}>
						<Typography className={classes.text}>Digital</Typography>
					</Grid>
				</Grid>
			</div>
		</>
	);
};

export default Stories;
